import type { DecisionAudit, PatternBased, RuleAction, RuleBased, RuleSet } from '@kkp-fraud/shared';
import { branchOfHitId, branchesOf, patternBranchesOf } from './branches';
import { familySegment, setRulesPath } from './ruleSetRoutes';

/**
 * The Dashboard's arithmetic, kept out of the component so every figure on the screen can be
 * checked without rendering it. Everything here is derived from data the console already
 * fetches — `GET /admin/stats`, `GET /admin/decisions` and the rule-set draft — and nothing
 * is invented to fill a gap: an hour with no decisions has no latency, not a latency of 0.
 */

type AnyRule = RuleBased | PatternBased;

/** Every action a rule can take, in row order: one per action row, or the rule's own. */
export function ruleActions(rule: AnyRule): RuleAction[] {
  return ('clauses' in rule ? branchesOf(rule) : patternBranchesOf(rule)).map((row) => row.action);
}

/**
 * The rules tab a rule id belongs to, by its family prefix — the primary set's (the Dashboard
 * counts the primary's rules). Wave 5: `/rule-sets/<primary>/…`; the pre-Wave-5 path, which
 * redirects, only while the primary is not known yet.
 */
export function rulePath(id: string, primaryId?: string | null): string | null {
  const ruleId = branchOfHitId(id)?.ruleId ?? id;
  const kind = ruleId.startsWith('RB-') ? 'rb' : ruleId.startsWith('PB-') ? 'pb' : null;
  if (!kind) return null;
  return primaryId ? setRulesPath(primaryId, kind) : `/rules/${familySegment(kind)}`;
}

function rulesOf(draft: RuleSet | null): AnyRule[] {
  return draft ? [...draft.ruleBased, ...draft.patternBased] : [];
}

export interface HitRow {
  /** The id as the stats window reported it — `RB-01`, or `RB-01#2` for one action row. */
  id: string;
  /** The rule the hit belongs to in the draft, if the draft still has it. */
  rule: AnyRule | null;
  hits: number;
  /** Percent of all rule hits in the window. One decision can hit several rules, so this is not a share of decisions. */
  share: number;
}

export function hitRows(byRule: Record<string, number> | undefined, draft: RuleSet | null): HitRow[] {
  // FAIL_SAFE is the engine's marker for a fail-safe answer, not a rule — it is counted in
  // the `degraded` figure, so it does not belong in a ranking of rules.
  const entries = Object.entries(byRule ?? {}).filter(([id, n]) => id !== 'FAIL_SAFE' && Number.isFinite(n) && n > 0);
  const sum = entries.reduce((acc, [, n]) => acc + n, 0);
  const byId = new Map(rulesOf(draft).map((r) => [r.id, r]));
  return entries
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([id, hits]) => ({
      id,
      rule: byId.get(branchOfHitId(id)?.ruleId ?? id) ?? null,
      hits,
      share: sum > 0 ? (hits / sum) * 100 : 0,
    }));
}

/** Enabled rules that did not fire once in the stats window — hits on any of their action rows count. */
export function neverHit(byRule: Record<string, number> | undefined, draft: RuleSet | null): AnyRule[] {
  const hitsByRule = new Map<string, number>();
  for (const [id, n] of Object.entries(byRule ?? {})) {
    const ruleId = branchOfHitId(id)?.ruleId ?? id;
    hitsByRule.set(ruleId, (hitsByRule.get(ruleId) ?? 0) + (Number.isFinite(n) ? n : 0));
  }
  return rulesOf(draft).filter((r) => r.enabled && (hitsByRule.get(r.id) ?? 0) === 0);
}

const timeOf = (d: DecisionAudit): number => {
  const ms = Date.parse(d.eventTimeUtc);
  return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY;
};

/** Decisions a cardholder felt (STEP_UP, DECLINE) or the fail-safe answered, newest first. */
export function needsAttention(decisions: readonly DecisionAudit[], max = 8): DecisionAudit[] {
  return decisions
    .filter((d) => d.decision === 'STEP_UP' || d.decision === 'DECLINE' || d.degraded)
    .map((d, i) => ({ d, i, at: timeOf(d) }))
    .sort((a, b) => b.at - a.at || a.i - b.i)
    .slice(0, max)
    .map(({ d }) => d);
}

export type TrendUnit = 'minute' | 'hour';

export interface TrendPoint {
  /** Bucket start, epoch ms. */
  start: number;
  count: number;
  /** p95 of engine latency in the bucket; `null` when the bucket had no decisions. */
  p95: number | null;
}

export interface Trend {
  unit: TrendUnit;
  points: TrendPoint[];
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
/** Up to two hours of decisions read per minute; anything longer, per hour. */
const MINUTE_SPAN_LIMIT = 2 * HOUR;
/** A sparkline does not need more than a month of hours; older buckets are dropped, newest kept. */
const MAX_POINTS = 720;

/** Nearest-rank percentile — the same definition an analyst gets from a sorted column. */
export function percentile(values: readonly number[], p: number): number | null {
  const xs = values.filter((v) => Number.isFinite(v)).sort((a, b) => a - b);
  if (xs.length === 0) return null;
  const rank = Math.min(xs.length, Math.max(1, Math.ceil((p / 100) * xs.length)));
  return xs[rank - 1]!;
}

/** Decisions per bucket and p95 engine latency per bucket, gaps included as empty buckets. */
export function trendOf(decisions: readonly DecisionAudit[]): Trend {
  const timed = decisions
    .map((d) => ({ at: Date.parse(d.eventTimeUtc), latency: d.engineLatencyMs }))
    .filter((x) => Number.isFinite(x.at));
  if (timed.length === 0) return { unit: 'minute', points: [] };

  let lo = Number.POSITIVE_INFINITY;
  let hi = Number.NEGATIVE_INFINITY;
  for (const { at } of timed) {
    if (at < lo) lo = at;
    if (at > hi) hi = at;
  }
  const unit: TrendUnit = hi - lo <= MINUTE_SPAN_LIMIT ? 'minute' : 'hour';
  const step = unit === 'minute' ? MINUTE : HOUR;
  const last = Math.floor(hi / step) * step;
  const first = Math.max(Math.floor(lo / step) * step, last - (MAX_POINTS - 1) * step);
  const n = Math.round((last - first) / step) + 1;

  const latencies: number[][] = Array.from({ length: n }, () => []);
  for (const { at, latency } of timed) {
    const i = Math.floor((at - first) / step);
    if (i < 0 || i >= n) continue;
    latencies[i]!.push(latency);
  }
  return {
    unit,
    points: latencies.map((ls, i) => ({ start: first + i * step, count: ls.length, p95: ls.length ? percentile(ls, 95) : null })),
  };
}
